import React, { useState, useRef } from "react";
import ExcelJS from "exceljs";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  LineController,
  BarController,
  Title,
  Filler,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { getDataFromMongoDb } from "../../services/mongod.data";
import { FirebaseDataService } from "../../services/firebase-data.service";
import handleToast from "../../helper/Toast";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  LineController,
  BarController,
  Title,
  Filler
);

const firebaseDataService = new FirebaseDataService();

// Helper functions for data transformations
const normalizeWeekKey = (weekValue) => {
  const raw = String(weekValue ?? "").trim();
  if (!raw) return "";

  const parsedDate = new Date(raw.replace(/\//g, "-"));
  if (Number.isNaN(parsedDate.getTime())) return raw;

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const sessionDistributionHandler = (data) => {
  let result = {
    percentVisitors: [],
    percentBooking: [],
    percentBuying: [],
    graphLabels: [],
    weeks: []
  };
  if (!data || !data[0]) return result;

  for (let i = 0; i < Object.keys(data[0]).length; i++) {
    let tempWeek = Object.keys(data[0])[i]
      .replace("(Week of", "")
      .replace(")", "");
    if (tempWeek !== "graph-options" && tempWeek !== "graph-backup") {
      result.weeks[i] = tempWeek;
    }
    const val = Object.values(data[0])[i];
    if (val && val[0] && val[0][0]) {
      result.percentVisitors[i] = val[0][0].data;
      result.percentBooking[i] = val[0][1].data;
      if (val[0][2] !== undefined) {
        result.percentBuying[i] = val[0][2].data;
      } else {
        result.percentBuying[i] = [0];
      }
      result.graphLabels[i] = val[1];
    }
  }

  return result;
};

const getPercentageLocal = (total, obtained) => {
  let percentages = [];
  for (let i = 0; i < total.length; i++) {
    percentages[i] = total[i] === 0 ? 0 : Math.round((obtained[i] * 100 / total[i]) * 100) / 100;
  }
  return percentages;
};

// Formatter functions
const formatUniqueVisitors = (uniqueBrowsers, engaged, weeks) => {
  return [
    ["Metric", ...weeks],
    ["Number of visitors engaged", ...engaged],
    ["Total Visitors", ...uniqueBrowsers],
  ];
};

const formatUniqueBookings = (visitors, percent, weeks) => {
  return [
    ["Metric", ...weeks],
    ["Number of visitors", ...visitors],
    ["% of Total Visitors", ...percent.map(p => p + "%")]
  ];
};

const formatUniqueBought = (bought, percent, weeks) => {
  return [
    ["Metric", ...weeks],
    ["Number of visitors buying", ...bought],
    ["% of add to cart", ...percent.map(p => p + "%")]
  ];
};

const SHOPPER_ORIGINS_CONFIGS = {
  google: {
    label: 'Google',
    borderColor: 'rgba(190, 70, 160, 0.6)',
    backgroundColor: 'rgba(230, 80, 140, 1)',
  },
  facebook: {
    label: 'Facebook',
    borderColor: 'rgba(32, 114, 255, 0.7)',
    backgroundColor: 'rgba(32, 114, 255, 1)',
  },
  linkedin: {
    label: 'LinkedIn',
    borderColor: 'rgba(240, 200, 50, 0.7)',
    backgroundColor: 'rgba(240, 200, 50, 1)',
  },
  instagram: {
    label: 'Instagram',
    borderColor: 'rgba(250, 170, 200, 0.7)',
    backgroundColor: 'rgba(250, 170, 200, 1)',
  },
  direct: {
    label: 'Direct',
    borderColor: 'rgba(0, 240, 110, 0.6)',
    backgroundColor: 'rgba(0, 210, 0, 1)',
  },
  linktree: {
    label: 'Linktree',
    borderColor: 'rgba(200, 0, 0, 0.7)',
    backgroundColor: 'rgba(200, 0, 0, 1)',
  },
  reddit: {
    label: 'Reddit',
    borderColor: 'rgba(0, 230, 220, 0.7)',
    backgroundColor: 'rgba(0, 210, 200, 1)',
  },
  others: {
    label: 'Others',
    borderColor: 'rgba(100, 0, 180, 1)',
    backgroundColor: 'rgba(190, 0, 240, 0.7)',
  },
  microsoft: {
    label: 'Microsoft',
    borderColor: 'rgba(0, 180, 50, 0.5)',
    backgroundColor: 'rgba(0, 120, 50, 1)',
  },
  hubspot: {
    label: 'HubSpot',
    borderColor: 'rgba(100, 150, 0, 0.5)',
    backgroundColor: 'rgba(130, 180, 0, 1)',
  },
  pinterest: {
    label: 'Pinterest',
    borderColor: 'rgba(250, 140, 0, 0.5)',
    backgroundColor: 'rgba(230, 120, 0, 1)',
  },
  afmc: {
    label: 'Affiliate Marketing (afmc)',
    borderColor: 'rgba(18, 28, 203, 0.5)',
    backgroundColor: '#244dc7',
  }
};

const getShopperOriginKey = (label) => {
  const norm = String(label || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  if (norm.includes("google")) return "google";
  if (norm.includes("facebook")) return "facebook";
  if (norm.includes("linkedin")) return "linkedin";
  if (norm.includes("instagram")) return "instagram";
  if (norm.includes("direct")) return "direct";
  if (norm.includes("linktree")) return "linktree";
  if (norm.includes("reddit")) return "reddit";
  if (norm.includes("others") || norm.includes("other")) return "others";
  if (norm.includes("microsoft")) return "microsoft";
  if (norm.includes("hubspot")) return "hubspot";
  if (norm.includes("pinterest")) return "pinterest";
  if (norm.includes("affiliate") || norm.includes("afmc")) return "afmc";
  return null;
};

const formatTrafficSource = (res) => {
  if (!res || !res[0] || !res[1]) return [];
  const datasets = res[0];
  const weeks = res[1];

  const mapped = {};
  datasets.forEach((dataset) => {
    const key = getShopperOriginKey(dataset.label);
    if (key) {
      mapped[key] = dataset.data || [];
    }
  });

  const orderedKeys = [
    "google",
    "facebook",
    "linkedin",
    "instagram",
    "direct",
    "linktree",
    "reddit",
    "others",
    "microsoft",
    "hubspot",
    "pinterest",
    "afmc"
  ];

  const rows = [["Origin", ...weeks]];
  orderedKeys.forEach((key) => {
    const config = SHOPPER_ORIGINS_CONFIGS[key];
    const data = mapped[key] || new Array(weeks.length).fill(0);
    rows.push([config.label, ...data]);
  });

  return rows;
};

const formatBrowserOSDevice = (res) => {
  if (!res || !res[0] || res[0].length === 0) return [];
  const labels = Object.keys(res[0][0] || {});
  const rows = res[0].map(item => Object.values(item));
  return [labels, ...rows];
};

const formatURLTable = (res) => {
  if (!res || !res[0] || res[0].length === 0) return [];
  const labels = Object.keys(res[0][0] || {});
  const rows = res[0].map(item => Object.values(item));
  return [labels, ...rows];
};

const formatVisitorEngagement = (weeks, percentVisitors, percentBooking, percentBuying, nameLabels) => {
  const rows = [];
  const firstLabels = nameLabels[0] || ["1", "2", "3", "4", "5+"];
  
  rows.push(["Buying Percentage Data"]);
  rows.push(["Week", ...firstLabels]);
  weeks.slice(0, 4).forEach((week, idx) => {
    if (week && percentBuying[idx]) {
      rows.push([`Week of ${week}`, ...percentBuying[idx]]);
    }
  });
  
  rows.push([]);
  rows.push(["Booking Percentage Data"]);
  rows.push(["Week", ...firstLabels]);
  weeks.slice(0, 4).forEach((week, idx) => {
    if (week && percentBooking[idx]) {
      rows.push([`Week of ${week}`, ...percentBooking[idx]]);
    }
  });
  
  rows.push([]);
  rows.push(["Visitor Percentage Data"]);
  rows.push(["Week", ...firstLabels]);
  weeks.slice(0, 4).forEach((week, idx) => {
    if (week && percentVisitors[idx]) {
      rows.push([`Week of ${week}`, ...percentVisitors[idx]]);
    }
  });
  
  return rows;
};

const buildNudgeWiseWeekMap = (data) => {
  const result = {};
  if (!data || typeof data !== "object") return result;
  Object.entries(data).forEach(([year, monthsObj]) => {
    if (year === "graph-options") return;
    if (!monthsObj || typeof monthsObj !== "object") return;
    Object.entries(monthsObj).forEach(([month, daysObj]) => {
      if (!daysObj || typeof daysObj !== "object") return;
      Object.entries(daysObj).forEach(([day, dayData]) => {
        const key = `${year}-${month}-${day}`;
        result[key] = Array.isArray(dayData) ? dayData : [];
      });
    });
  });
  return result;
};

const formatCouponsPerformance = (res) => {
  const rawData = res?.data || res || {};
  const yearDataObject = Array.isArray(rawData) ? rawData[1] : rawData;
  const formattedData = buildNudgeWiseWeekMap(yearDataObject);
  const weeks = Object.keys(formattedData).sort((a, b) => b.localeCompare(a));
  const rows = [
    ["Week", "Nudge Name", "Coupon Code", "Coupons Shown", "Added to Cart", "Sales Done"]
  ];
  weeks.forEach(week => {
    const weekRecords = formattedData[week] || [];
    weekRecords.forEach(rec => {
      rows.push([
        week,
        rec.nudgeName || rec.nudgeId || "",
        rec.couponCode || "",
        rec.couponShown || 0,
        rec.couponAddToCart || 0,
        rec.couponSalesDone || 0
      ]);
    });
  });
  return rows;
};

const formatNudgeWisePerformance = (res) => {
  const rawData = res?.data || res || {};
  const yearDataObject = Array.isArray(rawData) ? rawData[1] : rawData;
  const formattedData = buildNudgeWiseWeekMap(yearDataObject);
  const weeks = Object.keys(formattedData).sort((a, b) => b.localeCompare(a));
  
  const rows = [
    [
      "Week", 
      "Nudge Name", 
      "Coupon Code", 
      "Hypotheses Without Coupon (Users)", 
      "Hypotheses Without Coupon (Add to Cart)",
      "Null Hypotheses (Users)", 
      "Null Hypotheses (Add to Cart)", 
      "Coupon Shown (Users)", 
      "Coupon Shown (Add to Cart)"
    ]
  ];
  
  weeks.forEach(week => {
    const weekRecords = formattedData[week] || [];
    weekRecords.forEach(nudge => {
      const withoutCouponUsers = (nudge.eligibleUsers ?? 0) - (nudge.couponShown ?? 0);
      const withoutCouponATC = (nudge.eligibleUsersAddToCart ?? 0) - (nudge.shownUsersAddToCart ?? 0);
      const nullUsers = nudge.ineligibleUsers ?? 0;
      const nullATC = nudge.ineligibleUsersAddToCart ?? 0;
      const couponShownUsers = nudge.couponShown ?? 0;
      const couponShownATC = nudge.shownUsersAddToCart ?? 0;
      
      rows.push([
        week,
        nudge.nudgeName || nudge.couponCode || "Nudge",
        nudge.couponCode || "",
        withoutCouponUsers,
        withoutCouponATC,
        nullUsers,
        nullATC,
        couponShownUsers,
        couponShownATC
      ]);
    });
  });
  return rows;
};

// Offscreen chart data builders
const buildUniqueVisitorsChartData = (uniqueBrowsers, engaged, weeks) => {
  return {
    labels: weeks,
    datasets: [
      {
        label: 'Engaged',
        data: engaged,
        backgroundColor: 'rgba(0, 50, 248, 0.83)',
      },
      {
        label: 'Total',
        data: uniqueBrowsers,
        backgroundColor: 'rgba(0, 230, 248, 0.95)',
      },
    ]
  };
};

const buildVisitorEngagementChartData = (weekIdx, percentVisitors, percentBooking, percentBuying, nameLabels) => {
  const labels = nameLabels[0] || ["1", "2", "3", "4", "5+"];
  return {
    labels,
    datasets: [
      {
        label: '% of Visitors Buying in this Cohort',
        data: percentBuying[weekIdx] || [],
        type: 'line',
        fill: true,
        borderWidth: 3,
        yAxisID: 'y1',
        order: 2,
        pointRadius: 3,
        borderColor: (!percentBuying[weekIdx] || percentBuying[weekIdx].length <= 1) ? 'rgba(0,0,0,0)' : 'rgba(255, 216, 90, 1)',
        backgroundColor: (!percentBuying[weekIdx] || percentBuying[weekIdx].length <= 1) ? 'rgba(0,0,0,0)' : 'rgba(255, 216, 90, 0.5)',
      },
      {
        label: '% of Visitors Booking in this Cohort',
        data: percentBooking[weekIdx] || [],
        yAxisID: 'y1',
        borderColor: 'rgba(0, 50, 248, 0.83)',
        backgroundColor: 'rgba(0, 50, 248, 0.83)',
        order: 0,
        type: 'line',
        pointRadius: 3,
      },
      {
        label: '% of Visitors',
        data: percentVisitors[weekIdx] || [],
        backgroundColor: 'rgba(150, 167, 200, 0.99)',
        yAxisID: 'y',
        order: 1,
        type: 'bar',
      },
    ],
  };
};

const buildAddedToCartChartData = (visitors, percent, weeks) => {
  return {
    labels: weeks,
    datasets: [
      {
        label: "% of total visitors",
        data: percent,
        type: "line",
        borderColor: "rgba(0, 80, 248, 0.83)",
        backgroundColor: "rgba(0, 80, 248, 0.83)",
        yAxisID: "y1",
      },
      {
        label: "Number of visitors",
        data: visitors,
        backgroundColor: "rgba(250, 210, 90, 0.99)",
        type: "bar",
        yAxisID: "y",
      }
    ]
  };
};

const buildPurchasesChartData = (bought, percent, weeks) => {
  return {
    labels: weeks,
    datasets: [
      {
        label: '% of total visitors',
        data: percent,
        type: 'line',
        borderColor: 'rgba(250, 210, 90, 0.99)',
        backgroundColor: 'rgba(250, 210, 90, 0.45)',
        yAxisID: "y1",
      },
      {
        label: 'Number of visitors',
        data: bought,
        backgroundColor: 'rgba(250, 118, 85, 0.99)',
        type: 'bar',
        yAxisID: "y",
      }
    ]
  };
};

const buildShopperOriginsChartData = (res) => {
  if (!res || !res[0] || !res[1]) return null;
  const datasets = res[0];
  const weeks = res[1];

  const mapped = {};
  datasets.forEach((dataset) => {
    const key = getShopperOriginKey(dataset.label);
    if (key) {
      mapped[key] = dataset.data || [];
    }
  });

  const orderedKeys = [
    "google",
    "facebook",
    "linkedin",
    "instagram",
    "direct",
    "linktree",
    "reddit",
    "others",
    "microsoft",
    "hubspot",
    "pinterest",
    "afmc"
  ];

  const chartDatasets = orderedKeys.map((key) => {
    const config = SHOPPER_ORIGINS_CONFIGS[key];
    const data = mapped[key] || new Array(weeks.length).fill(0);
    return {
      label: config.label,
      type: 'line',
      data: data,
      borderColor: config.borderColor,
      backgroundColor: config.backgroundColor,
      hoverBackgroundColor: config.backgroundColor,
      lineTension: 0.2,
      pointRadius: 3,
      fill: false,
    };
  });

  return {
    labels: weeks,
    datasets: chartDatasets
  };
};

const buildSawNudgesChartData = (weeks, nudgeShown, nudgeAddToCart, addToCartPerc) => {
  return {
    labels: weeks,
    datasets: [
      {
        label: "Coupons shown and added to cart",
        data: nudgeAddToCart,
        backgroundColor: "rgba(230, 210, 20, 0.99)",
      },
      {
        label: "Coupons shown",
        data: nudgeShown,
        backgroundColor: "rgba(12, 100, 230, 0.99)",
      },
      {
        label: "% of visitors",
        data: addToCartPerc,
        type: "line",
        borderColor: "rgba(12, 200, 255, 0.99)",
        backgroundColor: "rgba(12, 200, 255, 0.99)",
      }
    ]
  };
};

const buildBoughtAfterNudgesChartData = (weeks, nudgeBought, boughtPerc, shownToBoughtPerc) => {
  return {
    labels: weeks,
    datasets: [
      {
        label: 'Coupons shown and finally bought',
        data: nudgeBought,
        backgroundColor: 'rgba(255, 100, 0, 0.99)',
      },
      {
        label: '% of buyer from add to cart nudge',
        data: boughtPerc,
        type: 'line',
        borderColor: 'rgba(12, 150, 255, 0.99)',
        backgroundColor: 'rgba(12, 150, 255, 0.99)',
      },
      {
        label: '% of buyer from nudge shown',
        data: shownToBoughtPerc,
        type: 'line',
        borderColor: 'rgba(230, 210, 20, 0.99)',
        backgroundColor: 'rgba(230, 210, 20, 0.99)',
      }
    ]
  };
};

export default function ExportClientReports({ user }) {
  const [exportLoading, setExportLoading] = useState(false);
  const [exportStatus, setExportStatus] = useState("");

  const uniqueVisitorsChartRef = useRef(null);
  const addedToCartChartRef = useRef(null);
  const purchasesChartRef = useRef(null);
  const shopperOriginsChartRef = useRef(null);
  const sawNudgesChartRef = useRef(null);
  const boughtAfterNudgesChartRef = useRef(null);
  const visitorEngagementChartRef1 = useRef(null);
  const visitorEngagementChartRef2 = useRef(null);
  const visitorEngagementChartRef3 = useRef(null);
  const visitorEngagementChartRef4 = useRef(null);

  const [selectedReports, setSelectedReports] = useState({
    unique_visitors: true,
    added_to_cart: true,
    purchases: true,
    shopper_origins: true,
    browsers_devices: true,
    url_traffic: true,
    url_traffic_2nd: true,
    url_traffic_booking: true,
    coupons_performance: true,
    ab_test_report: true,
    saw_nudges: true,
    bought_after_nudges: true,
    visitor_engagement: true,
  });

  const [chartDataState, setChartDataState] = useState({
    uniqueVisitors: null,
    addedToCart: null,
    purchases: null,
    shopperOrigins: null,
    sawNudges: null,
    boughtAfterNudges: null,
    visitorEngagement1: null,
    visitorEngagement2: null,
    visitorEngagement3: null,
    visitorEngagement4: null,
  });

  const reportOptions = [
    { key: "unique_visitors", title: "Unique Visitors", description: "Engaged vs total visitors graph and weekly breakdown." },
    { key: "added_to_cart", title: "Added to Cart", description: "Cart additions count, percentage and total visitors." },
    { key: "purchases", title: "Purchases", description: "Checkout purchase conversion rate and count." },
    { key: "visitor_engagement", title: "Visitor Engagement", description: "Visitor buying, booking, and session-engagement percentages over recent weeks." },
    { key: "shopper_origins", title: "Shopper Origins", description: "Referrer traffic channels (Google, Facebook, etc.)." },
    { key: "browsers_devices", title: "Browsers & Devices", description: "Visitor counts by browser, operating system, and device type." },
    { key: "url_traffic", title: "URL Traffic", description: "UTM landing pages and directory level traffic." },
    { key: "url_traffic_2nd", title: "URL Traffic (2nd Level)", description: "Traffic share and unique visitor counts for second-level directory URLs." },
    { key: "url_traffic_booking", title: "URL Traffic (Booking)", description: "Overall URL visit distribution of users that started booking." },
    { key: "coupons_performance", title: "Coupons Performance", description: "Shows, cart additions, and sales generated by nudge/coupon." },
    { key: "ab_test_report", title: "A/B Test Report", description: "Nudge conversion comparisons across hypotheses." },
    { key: "saw_nudges", title: "Intent Nudges Shown", description: "Shows, click-throughs, and cart additions by week." },
    { key: "bought_after_nudges", title: "Intent Nudges Purchases", description: "Performance, conversion rate, and buyer count after nudges." },
  ];

  const handleSelectAll = (val) => {
    setSelectedReports({
      unique_visitors: val,
      added_to_cart: val,
      purchases: val,
      shopper_origins: val,
      browsers_devices: val,
      url_traffic: val,
      url_traffic_2nd: val,
      url_traffic_booking: val,
      coupons_performance: val,
      ab_test_report: val,
      saw_nudges: val,
      bought_after_nudges: val,
      visitor_engagement: val,
    });
  };

  const handleExportAllData = async () => {
    if (!user) {
      handleToast("error", "No active client user found.");
      return;
    }

    const activeKeys = Object.keys(selectedReports).filter(k => selectedReports[k]);
    if (activeKeys.length === 0) {
      handleToast("error", "Please select at least one report to export.");
      return;
    }

    setExportLoading(true);
    setExportStatus("Fetching client analytics data...");
    try {
      const fetches = {};

      if (selectedReports.unique_visitors || selectedReports.visitor_engagement) {
        fetches.sessionDistribution = new Promise((res) => {
          firebaseDataService.getStats(null, user, "graph-sessionDistribution", "noncadenceBarchart").subscribe({
            next: (data) => res(data),
            error: () => res(null)
          });
        });
      }

      if (selectedReports.unique_visitors) {
        fetches.uniqueBnDev = new Promise((res) => {
          firebaseDataService.getStats(null, user, "graph-uniqueBnDev", "barchart").subscribe({
            next: (data) => res(data),
            error: () => res(null)
          });
        });
      }

      if (selectedReports.added_to_cart || selectedReports.purchases) {
        fetches.uniqueBookings = new Promise((res) => {
          firebaseDataService.getStats(null, user, "graph-uniqueBookings", "barchart").subscribe({
            next: (data) => res(data),
            error: () => res(null)
          });
        });
      }

      if (selectedReports.shopper_origins) {
        fetches.trafficSource = new Promise((res) => {
          firebaseDataService.getStats(null, user, "graph-socialCounts", "socialCounts").subscribe({
            next: (data) => res(data),
            error: () => res(null)
          });
        });
      }

      if (selectedReports.browsers_devices) {
        fetches.browserOS = new Promise((res) => {
          firebaseDataService.getStats(null, user, "graph-uniqueBrowserOSDevice", "tablechart").subscribe({
            next: (data) => res(data),
            error: () => res(null)
          });
        });
      }

      if (selectedReports.url_traffic) {
        fetches.url1stLevel = new Promise((res) => {
          firebaseDataService.getStats(null, user, "graph-uniqueURL1stLevelDir", "tablechart").subscribe({
            next: (data) => res(data),
            error: () => res(null)
          });
        });
      }

      if (selectedReports.url_traffic_2nd) {
        fetches.url2ndLevel = new Promise((res) => {
          firebaseDataService.getStats(null, user, "graph-uniqueURL2ndLevelDir", "tablechart").subscribe({
            next: (data) => res(data),
            error: () => res(null)
          });
        });
      }

      if (selectedReports.url_traffic_booking) {
        fetches.urlBookingOverall = new Promise((res) => {
          firebaseDataService.getStats(null, user, "graph-uniqueURLbookInProOverall", "tablechart").subscribe({
            next: (data) => res(data),
            error: () => res(null)
          });
        });
      }

      if (selectedReports.coupons_performance) {
        fetches.coupons = getDataFromMongoDb(user, "graph-couponWise-personaPerformance").catch(() => null);
      }

      if (selectedReports.ab_test_report) {
        fetches.nudges = getDataFromMongoDb(user, "graph-nudgeWise-personaPerformance").catch(() => null);
      }

      if (selectedReports.saw_nudges || selectedReports.bought_after_nudges) {
        fetches.persona = new Promise((res) => {
          firebaseDataService.getStats(null, user, "graph-persona", "barchart").subscribe({
            next: (data) => res(data),
            error: () => res(null)
          });
        });
      }

      const results = await Promise.all(Object.values(fetches));
      const keys = Object.keys(fetches);
      const resData = {};
      keys.forEach((key, idx) => {
        resData[key] = results[idx];
      });

      // ----------------------------------------------------
      // Compute dashboard aligned variables
      // ----------------------------------------------------
      
      // 1. Unique Visitors calculation
      let uniqueBrowsers = [], engaged = [], uniqueVisitorsWeeks = [];
      if (selectedReports.unique_visitors && resData.uniqueBnDev && resData.sessionDistribution) {
        uniqueBrowsers = resData.uniqueBnDev[0]?.[0]?.["data"] || [];
        uniqueVisitorsWeeks = resData.uniqueBnDev[1] || [];
        
        const resultData = sessionDistributionHandler(resData.sessionDistribution);
        const rawDistWeeks = resultData.weeks;
        const rawDistPercent = resultData.percentVisitors;
        const distWeeks = [];
        const distPercent = [];
        for (let i = 0; i < rawDistWeeks.length; i++) {
          if (rawDistWeeks[i] !== undefined && rawDistPercent[i] !== undefined) {
            distWeeks.push(rawDistWeeks[i]);
            distPercent.push(rawDistPercent[i]);
          }
        }
        distWeeks.reverse();
        distPercent.reverse();

        const percentVisitorsByWeek = {};
        distWeeks.forEach((week, idx) => {
          const key = normalizeWeekKey(week);
          const percentValue = Array.isArray(distPercent[idx]) ? distPercent[idx][0] : distPercent[idx];
          percentVisitorsByWeek[key] = Number(percentValue) || 0;
        });

        engaged = uniqueVisitorsWeeks.map((week, idx) => {
          const key = normalizeWeekKey(week);
          const percent = percentVisitorsByWeek[key] || 0;
          const totalUsers = Number(uniqueBrowsers[idx]) || 0;
          return Math.round(totalUsers * (percent / 100));
        });
      }

      // 2. Added to Cart calculation
      let bookingsVisitors = [], bookingsPercentVisitors = [], bookingsWeeks = [];
      if (resData.uniqueBookings) {
        bookingsVisitors = resData.uniqueBookings[0]?.[0]?.["data"] || [];
        bookingsPercentVisitors = resData.uniqueBookings[0]?.[1]?.["data"] || [];
        bookingsWeeks = resData.uniqueBookings[1] || [];
      }

      // 3. Purchases calculation
      let boughtVisitors = [], boughtPercentVisitors = [];
      if (resData.uniqueBookings) {
        const rawData = resData.uniqueBookings;
        if (rawData[0] && rawData[0].length > 2) {
          boughtVisitors = rawData[0][1].data.map((item, i) => {
            const visitors = rawData[0][0].data[i] ?? 0;
            const percent = rawData[0][2].data[i] ?? 0;
            return Math.round(visitors * percent / 100);
          });
          boughtPercentVisitors = rawData[0][2].data.map(item => item ?? 0);
        }
      }

      // 4. Visitor Engagement calculation
      let visitorEngagementWeeks = [], percentVisitors = [], percentBooking = [], percentBuying = [], nameLabels = [];
      if (resData.sessionDistribution) {
        const resultData = sessionDistributionHandler(resData.sessionDistribution);
        visitorEngagementWeeks = [...resultData.weeks].reverse();
        percentVisitors = [...resultData.percentVisitors].reverse();
        percentBooking = [...resultData.percentBooking].reverse();
        percentBuying = [...resultData.percentBuying].reverse();
        nameLabels = [...resultData.graphLabels].reverse();
      }

      // 5. Intent Nudges calculation
      let nudgeShown = [], nudgeAddToCart = [], nudgeBought = [], personaWeeks = [];
      let addToCartPerc = [], boughtPerc = [], shownToBoughtPerc = [];
      if (resData.persona) {
        const result = resData.persona;
        personaWeeks = result[1] || [];
        if (result[0] && result[0].length >= 3) {
          nudgeShown = result[0][0]?.data || [];
          nudgeAddToCart = result[0][1]?.data || [];
          nudgeBought = result[0][2]?.data || [];
          addToCartPerc = getPercentageLocal(nudgeShown, nudgeAddToCart);
          boughtPerc = getPercentageLocal(nudgeAddToCart, nudgeBought);
          shownToBoughtPerc = getPercentageLocal(nudgeShown, nudgeBought);
        }
      }

      // ----------------------------------------------------
      // Render offscreen charts to trigger canvas drawings
      // ----------------------------------------------------
      setExportStatus("Rendering graph views...");
      const nextChartData = {};
      if (selectedReports.unique_visitors && resData.uniqueBnDev) {
        nextChartData.uniqueVisitors = buildUniqueVisitorsChartData(uniqueBrowsers, engaged, uniqueVisitorsWeeks);
      }
      if (selectedReports.added_to_cart && resData.uniqueBookings) {
        nextChartData.addedToCart = buildAddedToCartChartData(bookingsVisitors, bookingsPercentVisitors, bookingsWeeks);
      }
      if (selectedReports.purchases && resData.uniqueBookings) {
        nextChartData.purchases = buildPurchasesChartData(boughtVisitors, boughtPercentVisitors, bookingsWeeks);
      }
      if (selectedReports.shopper_origins && resData.trafficSource) {
        nextChartData.shopperOrigins = buildShopperOriginsChartData(resData.trafficSource);
      }
      if (selectedReports.saw_nudges && resData.persona) {
        nextChartData.sawNudges = buildSawNudgesChartData(personaWeeks, nudgeShown, nudgeAddToCart, addToCartPerc);
      }
      if (selectedReports.bought_after_nudges && resData.persona) {
        nextChartData.boughtAfterNudges = buildBoughtAfterNudgesChartData(personaWeeks, nudgeBought, boughtPerc, shownToBoughtPerc);
      }
      if (selectedReports.visitor_engagement && resData.sessionDistribution) {
        nextChartData.visitorEngagement1 = buildVisitorEngagementChartData(0, percentVisitors, percentBooking, percentBuying, nameLabels);
        nextChartData.visitorEngagement2 = buildVisitorEngagementChartData(1, percentVisitors, percentBooking, percentBuying, nameLabels);
        nextChartData.visitorEngagement3 = buildVisitorEngagementChartData(2, percentVisitors, percentBooking, percentBuying, nameLabels);
        nextChartData.visitorEngagement4 = buildVisitorEngagementChartData(3, percentVisitors, percentBooking, percentBuying, nameLabels);
      }

      setChartDataState(nextChartData);

      // Wait for React rendering to finish and Canvas drawings to register
      await new Promise((resolve) => setTimeout(resolve, 800));

      setExportStatus("Capturing graph images...");
      const getChartImageBase64 = (ref) => {
        if (ref && ref.current && ref.current.canvas) {
          return ref.current.canvas.toDataURL("image/png");
        }
        return null;
      };

      const uniqueVisitorsImg = getChartImageBase64(uniqueVisitorsChartRef);
      const addedToCartImg = getChartImageBase64(addedToCartChartRef);
      const purchasesImg = getChartImageBase64(purchasesChartRef);
      const shopperOriginsImg = getChartImageBase64(shopperOriginsChartRef);
      const sawNudgesImg = getChartImageBase64(sawNudgesChartRef);
      const boughtAfterNudgesImg = getChartImageBase64(boughtAfterNudgesChartRef);

      let visitorEngagementImg = null;
      if (selectedReports.visitor_engagement && visitorEngagementChartRef1.current && visitorEngagementChartRef2.current && visitorEngagementChartRef3.current && visitorEngagementChartRef4.current) {
        const canvas1 = visitorEngagementChartRef1.current.canvas;
        const canvas2 = visitorEngagementChartRef2.current.canvas;
        const canvas3 = visitorEngagementChartRef3.current.canvas;
        const canvas4 = visitorEngagementChartRef4.current.canvas;

        const w = canvas1.width;
        const h = canvas1.height;
        const headerHeight = 40;

        const mergedCanvas = document.createElement('canvas');
        mergedCanvas.width = 2 * w;
        mergedCanvas.height = 2 * h + 2 * headerHeight;

        const ctx = mergedCanvas.getContext('2d');
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, mergedCanvas.width, mergedCanvas.height);

        ctx.font = "bold 16px Arial";
        ctx.fillStyle = "#374151";

        if (visitorEngagementWeeks && visitorEngagementWeeks.length >= 4) {
          ctx.fillText(`Week of ${visitorEngagementWeeks[0]}`, 20, 26);
          ctx.fillText(`Week of ${visitorEngagementWeeks[1]}`, w + 20, 26);
          ctx.fillText(`Week of ${visitorEngagementWeeks[2]}`, 20, h + headerHeight + 26);
          ctx.fillText(`Week of ${visitorEngagementWeeks[3]}`, w + 20, h + headerHeight + 26);
        }

        ctx.drawImage(canvas1, 0, headerHeight);
        ctx.drawImage(canvas2, w, headerHeight);
        ctx.drawImage(canvas3, 0, h + 2 * headerHeight);
        ctx.drawImage(canvas4, w, h + 2 * headerHeight);

        visitorEngagementImg = mergedCanvas.toDataURL("image/png");
      }

      // ----------------------------------------------------
      // Excel compilation and sheets generation
      // ----------------------------------------------------
      setExportStatus("Compiling multi-tab Excel workbook...");
      const workbook = new ExcelJS.Workbook();

      const populateSheet = (worksheet, title, data, imgBase64) => {
        if (!data || data.length === 0) return;
        
        const titleRow = worksheet.addRow([`CausalFunnel Report - ${title}`]);
        titleRow.font = { name: 'Arial', size: 14, bold: true };
        const timeRow = worksheet.addRow(['Generated On: ' + new Date().toLocaleString()]);
        timeRow.font = { name: 'Arial', size: 10, italic: true };
        worksheet.addRow([]);

        data.forEach((row, idx) => {
          const addedRow = worksheet.addRow(row);
          if (idx === 0) {
            addedRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            addedRow.eachCell(cell => {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF1E3A8A' }
              };
              cell.alignment = { horizontal: 'center' };
            });
          }
        });

        worksheet.columns.forEach(column => {
          let maxLen = 12;
          column.eachCell({ includeEmpty: false }, cell => {
            const valStr = cell.value ? cell.value.toString() : '';
            if (valStr.length > maxLen) {
              maxLen = valStr.length;
            }
          });
          column.width = maxLen + 3;
        });

        if (imgBase64) {
          const imageId = workbook.addImage({
            base64: imgBase64,
            extension: 'png',
          });
          const startRow = data.length + 5;
          worksheet.addImage(imageId, {
            tl: { col: 0, row: startRow },
            ext: { width: 650, height: 350 }
          });
        }
      };

      // 1. Unique Visitors
      if (selectedReports.unique_visitors && resData.uniqueBnDev) {
        const sheetData = formatUniqueVisitors(uniqueBrowsers, engaged, uniqueVisitorsWeeks);
        const ws = workbook.addWorksheet("Unique Visitors");
        populateSheet(ws, "Unique Visitors", sheetData, uniqueVisitorsImg);
      }

      // 2. Added to Cart
      if (selectedReports.added_to_cart && resData.uniqueBookings) {
        const sheetData = formatUniqueBookings(bookingsVisitors, bookingsPercentVisitors, bookingsWeeks);
        const ws = workbook.addWorksheet("Added to Cart");
        populateSheet(ws, "Added to Cart", sheetData, addedToCartImg);
      }

      // 3. Purchases
      if (selectedReports.purchases && resData.uniqueBookings) {
        const sheetData = formatUniqueBought(boughtVisitors, boughtPercentVisitors, bookingsWeeks);
        const ws = workbook.addWorksheet("Purchases");
        populateSheet(ws, "Purchases", sheetData, purchasesImg);
      }

      // 4. Visitor Engagement
      if (selectedReports.visitor_engagement && resData.sessionDistribution) {
        const sheetData = formatVisitorEngagement(visitorEngagementWeeks, percentVisitors, percentBooking, percentBuying, nameLabels);
        const ws = workbook.addWorksheet("Visitor Engagement");
        populateSheet(ws, "Visitor Engagement", sheetData, visitorEngagementImg);
      }

      // 5. Shopper Origins
      if (selectedReports.shopper_origins && resData.trafficSource) {
        const sheetData = formatTrafficSource(resData.trafficSource);
        const ws = workbook.addWorksheet("Shopper Origins");
        populateSheet(ws, "Shopper Origins", sheetData, shopperOriginsImg);
      }

      // 6. Browsers & Devices
      if (selectedReports.browsers_devices && resData.browserOS) {
        const sheetData = formatBrowserOSDevice(resData.browserOS);
        const ws = workbook.addWorksheet("Browsers & Devices");
        populateSheet(ws, "Browsers & Devices", sheetData, null);
      }

      // 7. URL Traffic (1st Level)
      if (selectedReports.url_traffic && resData.url1stLevel) {
        const sheetData = formatURLTable(resData.url1stLevel);
        const ws = workbook.addWorksheet("URL Traffic (1st Level)");
        populateSheet(ws, "URL Traffic (1st Level)", sheetData, null);
      }

      // 8. URL Traffic (2nd Level)
      if (selectedReports.url_traffic_2nd && resData.url2ndLevel) {
        const sheetData = formatURLTable(resData.url2ndLevel);
        const ws = workbook.addWorksheet("URL Traffic (2nd Level)");
        populateSheet(ws, "URL Traffic (2nd Level)", sheetData, null);
      }

      // 9. URL Traffic (Booking)
      if (selectedReports.url_traffic_booking && resData.urlBookingOverall) {
        const sheetData = formatURLTable(resData.urlBookingOverall);
        const ws = workbook.addWorksheet("URL Traffic (Booking)");
        populateSheet(ws, "URL Traffic (Booking)", sheetData, null);
      }

      // 7. Coupons Performance
      if (selectedReports.coupons_performance && resData.coupons) {
        const sheetData = formatCouponsPerformance(resData.coupons);
        const ws = workbook.addWorksheet("Coupons Performance");
        populateSheet(ws, "Coupons Performance", sheetData, null);
      }

      // 8. AB Test Report
      if (selectedReports.ab_test_report && resData.nudges) {
        const sheetData = formatNudgeWisePerformance(resData.nudges);
        const ws = workbook.addWorksheet("AB Test Report");
        populateSheet(ws, "AB Test Report", sheetData, null);
      }

      // 9. Intent Nudges Shown
      if (selectedReports.saw_nudges && resData.persona) {
        const sheetData = [
          ["Metric", ...personaWeeks],
          ["% of visitors", ...addToCartPerc.map(val => val + "%")],
          ["Coupons shown and added to cart", ...nudgeAddToCart],
          ["Coupons shown", ...nudgeShown]
        ];
        const ws = workbook.addWorksheet("Intent Nudges Shown");
        populateSheet(ws, "Intent Nudges Shown", sheetData, sawNudgesImg);
      }

      // 10. Intent Nudges Purchases
      if (selectedReports.bought_after_nudges && resData.persona) {
        const sheetData = [
          ['Metric', ...personaWeeks],
          ['% of buyer from add to cart nudge', ...boughtPerc.map(val => val + "%")],
          ['% of buyer from nudge shown', ...shownToBoughtPerc.map(val => val + "%")],
          ['Coupons shown and finally bought', ...nudgeBought]
        ];
        const ws = workbook.addWorksheet("Intent Nudges Purchases");
        populateSheet(ws, "Intent Nudges Purchases", sheetData, boughtAfterNudgesImg);
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${user}_analytics_report.xlsx`;
      link.click();
      handleToast("success", "Analytics reports exported successfully!");
    } catch (error) {
      console.error("Failed to generate multi-tab Excel:", error);
      handleToast("error", "Failed to generate report export.");
    } finally {
      setExportLoading(false);
      setExportStatus("");
      setChartDataState({
        uniqueVisitors: null,
        addedToCart: null,
        purchases: null,
        shopperOrigins: null,
        sawNudges: null,
        boughtAfterNudges: null,
        visitorEngagement1: null,
        visitorEngagement2: null,
        visitorEngagement3: null,
        visitorEngagement4: null,
      });
    }
  };

  return (
    <>
      {exportLoading && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex flex-col justify-center items-center gap-3">
          <div className="bg-white p-6 rounded-xl shadow-2xl flex flex-col items-center gap-4">
            <svg className="animate-spin h-10 w-10 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-gray-800 font-semibold">{exportStatus}</span>
          </div>
        </div>
      )}

      {/* Export Client Reports Selection & Download Section */}
      <div className="mb-4 rounded-md border-2">
        <div className="pl-3 bg-gray-300 py-2 font-semibold">Export Client Reports</div>
        <div className="p-4">
          <p className="text-sm text-gray-600 mb-4">
            Select the reports you want to compile and export into a single multi-tab Excel spreadsheet.
          </p>
          <div className="flex gap-4 mb-4 text-xs font-semibold text-blue-600">
            <button type="button" onClick={() => handleSelectAll(true)} className="hover:underline">
              Select All
            </button>
            <span className="text-gray-300">|</span>
            <button type="button" onClick={() => handleSelectAll(false)} className="hover:underline">
              Deselect All
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {reportOptions.map((opt) => (
              <label
                key={opt.key}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition select-none ${
                  selectedReports[opt.key]
                    ? "border-emerald-500 bg-emerald-50/30"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedReports[opt.key]}
                  onChange={() => {
                    setSelectedReports(prev => ({
                      ...prev,
                      [opt.key]: !prev[opt.key]
                    }));
                  }}
                  style={{ borderRadius: "50%" }}
                  className="h-5 w-5 rounded-full border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                />
                <div>
                  <span className="font-semibold text-gray-800 block text-sm">{opt.title}</span>
                  <p className="text-xs text-gray-500">{opt.description}</p>
                </div>
              </label>
            ))}
          </div>
          <div>
            <button
              type="button"
              onClick={handleExportAllData}
              disabled={exportLoading}
              className={`px-5 py-2.5 rounded-md text-white font-semibold text-sm transition-all ${
                exportLoading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-emerald-600 hover:bg-emerald-700 shadow-md hover:shadow-lg"
              }`}
            >
              {exportLoading ? "Exporting Reports..." : "Export Client Reports"}
            </button>
          </div>
        </div>
      </div>

      {/* Offscreen hidden charts for image capture */}
      <div style={{ position: "absolute", left: "-9999px", top: "-9999px", width: "650px", height: "350px" }}>
        {chartDataState.uniqueVisitors && (
          <Chart ref={uniqueVisitorsChartRef} type="bar" data={chartDataState.uniqueVisitors} options={{ responsive: true, maintainAspectRatio: false, animation: false, plugins: { legend: { position: 'top' } }, scales: { x: { stacked: true, title: { display: true, text: 'Week' } }, y: { title: { display: true, text: 'Number of users' } } } }} />
        )}
        {chartDataState.addedToCart && (
          <Chart ref={addedToCartChartRef} type="bar" data={chartDataState.addedToCart} options={{ responsive: true, maintainAspectRatio: false, animation: false, plugins: { legend: { position: 'top' } }, scales: { x: { title: { display: true, text: 'Week' } }, y: { type: 'linear', position: 'left', title: { display: true, text: 'Number of visitors' } }, y1: { type: 'linear', position: 'right', min: 0, title: { display: true, text: '% of total visitors' } } } }} />
        )}
        {chartDataState.purchases && (
          <Chart ref={purchasesChartRef} type="bar" data={chartDataState.purchases} options={{ responsive: true, maintainAspectRatio: false, animation: false, plugins: { legend: { position: 'top' } }, scales: { x: { title: { display: true, text: 'Week' } }, y: { type: 'linear', position: 'left', title: { display: true, text: 'Number of buyers' } }, y1: { type: 'linear', position: 'right', min: 0, title: { display: true, text: '% of add to cart' } } } }} />
        )}
        {chartDataState.shopperOrigins && (
          <Chart
            ref={shopperOriginsChartRef}
            type="line"
            data={chartDataState.shopperOrigins}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              animation: false,
              plugins: {
                legend: {
                  position: "right",
                  labels: {
                    usePointStyle: true,
                    useBorderRadius: true,
                  },
                },
              },
              scales: {
                x: { title: { display: true, text: "Week" } },
                y: { title: { display: true, text: "Number of shoppers" } },
              },
            }}
          />
        )}
        {chartDataState.visitorEngagement1 && (
          <Chart ref={visitorEngagementChartRef1} type="bar" data={chartDataState.visitorEngagement1} options={{ responsive: true, maintainAspectRatio: false, animation: false, plugins: { legend: { display: false } }, scales: { x: { title: { display: true, text: 'Number of Sessions' } }, y: { type: 'linear', position: 'left', title: { display: true, text: '% of Visitors' } }, y1: { type: 'linear', position: 'right', min: 0, title: { display: true, text: '% of Visitors' } } } }} />
        )}
        {chartDataState.visitorEngagement2 && (
          <Chart ref={visitorEngagementChartRef2} type="bar" data={chartDataState.visitorEngagement2} options={{ responsive: true, maintainAspectRatio: false, animation: false, plugins: { legend: { display: false } }, scales: { x: { title: { display: true, text: 'Number of Sessions' } }, y: { type: 'linear', position: 'left', title: { display: true, text: '% of Visitors' } }, y1: { type: 'linear', position: 'right', min: 0, title: { display: true, text: '% of Visitors' } } } }} />
        )}
        {chartDataState.visitorEngagement3 && (
          <Chart ref={visitorEngagementChartRef3} type="bar" data={chartDataState.visitorEngagement3} options={{ responsive: true, maintainAspectRatio: false, animation: false, plugins: { legend: { display: false } }, scales: { x: { title: { display: true, text: 'Number of Sessions' } }, y: { type: 'linear', position: 'left', title: { display: true, text: '% of Visitors' } }, y1: { type: 'linear', position: 'right', min: 0, title: { display: true, text: '% of Visitors' } } } }} />
        )}
        {chartDataState.visitorEngagement4 && (
          <Chart ref={visitorEngagementChartRef4} type="bar" data={chartDataState.visitorEngagement4} options={{ responsive: true, maintainAspectRatio: false, animation: false, plugins: { legend: { display: false } }, scales: { x: { title: { display: true, text: 'Number of Sessions' } }, y: { type: 'linear', position: 'left', title: { display: true, text: '% of Visitors' } }, y1: { type: 'linear', position: 'right', min: 0, title: { display: true, text: '% of Visitors' } } } }} />
        )}
        {chartDataState.sawNudges && (
          <Chart ref={sawNudgesChartRef} type="bar" data={chartDataState.sawNudges} options={{ responsive: true, maintainAspectRatio: false, animation: false, plugins: { legend: { position: 'top' } }, scales: { x: { stacked: true, title: { display: true, text: 'Week' } }, y: { stacked: true, title: { display: true, text: 'Number of unique shoppers' } }, y1: { position: 'right', title: { display: true, text: '% of visitors who added to cart' } } } }} />
        )}
        {chartDataState.boughtAfterNudges && (
          <Chart ref={boughtAfterNudgesChartRef} type="bar" data={chartDataState.boughtAfterNudges} options={{ responsive: true, maintainAspectRatio: false, animation: false, plugins: { legend: { position: 'top' } }, scales: { x: { stacked: true, title: { display: true, text: 'Week' } }, y: { title: { display: true, text: 'Number of unique shoppers' } }, y1: { position: 'right', title: { display: true, text: '% of buyer from nudge' } } } }} />
        )}
      </div>
    </>
  );
}
